// ===================
// MEDIUM AI CARD PLAY
// ===================

import type { Card, Artist } from '../../../types/game'
import type { AIDecisionContext, CardEvaluation } from '../../types'
import { MediumAICardValuation } from './valuation'
import { calculateFifthCardBonus } from './fifth-card-bonus'
import { canPlayDoubleSafely } from './calculate-hijack-probability'
import { createProbabilityUtils } from '../../utils'

/**
 * Medium AI card selection - chooses optimal cards based on EV
 *
 * The AI considers:
 * - Strategic value of ending the round by playing a 5th card
 * - Hijack risk when playing double cards without a matching pair
 * - Overall expected value of each card in hand
 */
export class MediumAICardPlay {
  private valuation: MediumAICardValuation
  private probability = createProbabilityUtils()

  private decisionCount = 0
  private evCalculationCount = 0

  constructor(seed?: number) {
    this.valuation = new MediumAICardValuation()
  }

  /**
   * Select optimal card to auction
   *
   * Strategy:
   * 1. Check if AI can end the round (has a 5th card)
   * 2. If yes, calculate EV of ending round vs continuing
   * 3. Choose the option with higher EV
   */
  async selectOptimalCard(context: AIDecisionContext): Promise<{ card: Card; evaluation: CardEvaluation } | null> {
    const { gameState } = context
    const player = gameState.players[context.playerIndex]

    if (player.hand.length === 0) {
      return null
    }

    // STEP 1: Can I end the round? (Do I have a 5th card?)
    const canEndRound = this.canEndRound(player.hand, gameState)

    if (canEndRound) {
      // STEP 2: Should I end the round?
      const endRoundDecision = await this.evaluateEndingRound(player.hand, context)

      // STEP 3: Compare EV of ending vs continuing
      const continueDecision = await this.evaluateContinuingRound(player.hand, context)

      if (endRoundDecision.bestEV >= continueDecision.bestEV) {
        // End the round - play the 5th card
        return endRoundDecision.bestCard
      }
    }

    // STEP 4: Not ending round - evaluate all cards normally (excluding 5th cards)
    const evaluatedCards = await this.evaluateHandForContinuing(player.hand, context)

    // Filter out cards that would end the round (we already decided not to)
    const safeCards = this.filterSafeCards(evaluatedCards, context)

    if (safeCards.length === 0) {
      // No safe cards, return least risky
      return evaluatedCards[0] || null
    }

    // Select card with highest strategic value
    let bestCard = this.selectBestCard(safeCards, context)

    // STEP 5: Check if best card is a double auction with hijack risk
    if (bestCard && bestCard.card.auctionType === 'double') {
      const safeFromHijack = await this.checkDoubleCardSafety(bestCard.card, player.hand, context)

      if (!safeFromHijack) {
        // Double card is unsafe - try to use second best card
        if (safeCards.length > 1) {
          // safeCards is already sorted by strategic value
          // The second best card should be at index 1, unless bestCard is not at index 0
          const bestIndex = safeCards.findIndex(c => c.card.id === bestCard!.card.id)
          if (bestIndex === 0 && safeCards.length > 1) {
            bestCard = safeCards[1]  // Second best
          } else if (bestIndex > 0) {
            bestCard = safeCards[0]  // First card is better than the double
          }
        }
        // If no second option, we'll play the risky double anyway (last resort)
      }
    }

    this.decisionCount++
    return bestCard
  }

  /**
   * Check if player has any 5th card that could end the round
   */
  private canEndRound(hand: Card[], gameState: GameState): boolean {
    return hand.some(card => {
      const currentCount = gameState.round.cardsPlayedPerArtist[card.artist] || 0
      return currentCount === 4
    })
  }

  /**
   * Evaluate the option of ending the round by playing a 5th card
   * Returns the best 5th card and its EV (including round-end bonus)
   */
  private async evaluateEndingRound(
    hand: Card[],
    context: AIDecisionContext
  ): Promise<{ bestCard: { card: Card; evaluation: CardEvaluation } | null; bestEV: number }> {
    const evaluatedCards: Array<{ card: Card; evaluation: CardEvaluation }> = []

    // Only evaluate 5th cards
    for (const card of hand) {
      const currentCount = context.gameState.round.cardsPlayedPerArtist[card.artist] || 0

      if (currentCount === 4) {
        // Get normal EV
        const evaluation = this.valuation.evaluateCard(card, context.gameState, context.playerIndex)

        // Add round-end bonus
        const roundEndBonus = calculateFifthCardBonus(context.gameState, context.playerIndex)

        // Clone and add bonus
        const evaluationWithBonus = {
          ...evaluation,
          strategicValue: evaluation.strategicValue + roundEndBonus,
          reasoning: evaluation.reasoning + ` + ${roundEndBonus.toFixed(1)}k round-end bonus`
        }

        evaluatedCards.push({ card, evaluation: evaluationWithBonus })
      }
    }

    // Find best 5th card
    evaluatedCards.sort((a, b) => b.evaluation.strategicValue - a.evaluation.strategicValue)

    return {
      bestCard: evaluatedCards[0] || null,
      bestEV: evaluatedCards[0]?.evaluation.strategicValue || 0
    }
  }

  /**
   * Evaluate the option of continuing the round
   * Returns the best non-5th card and its EV
   */
  private async evaluateContinuingRound(
    hand: Card[],
    context: AIDecisionContext
  ): Promise<{ bestCard: { card: Card; evaluation: CardEvaluation } | null; bestEV: number }> {
    // Evaluate all cards EXCEPT 5th cards
    const evaluatedCards = await this.evaluateHandForContinuing(hand, context)

    // Find best card
    evaluatedCards.sort((a, b) => b.evaluation.strategicValue - a.evaluation.strategicValue)

    return {
      bestCard: evaluatedCards[0] || null,
      bestEV: evaluatedCards[0]?.evaluation.strategicValue || 0
    }
  }

  /**
   * Evaluate all cards in hand (for continuing the round)
   * Excludes 5th cards since we're evaluating the "continue" option
   */
  private async evaluateHandForContinuing(hand: Card[], context: AIDecisionContext): Promise<Array<{ card: Card; evaluation: CardEvaluation }>> {
    const evaluatedCards: Array<{ card: Card; evaluation: CardEvaluation }> = []

    for (const card of hand) {
      const currentCount = context.gameState.round.cardsPlayedPerArtist[card.artist] || 0

      // Skip 5th cards when evaluating "continue" option
      if (currentCount === 4) {
        continue
      }

      const evaluation = this.valuation.evaluateCard(card, context.gameState, context.playerIndex)
      evaluatedCards.push({ card, evaluation })
      this.evCalculationCount++
    }

    return evaluatedCards
  }

  /**
   * Filter out cards that would be strategically bad to play
   * Note: 5th cards are now evaluated with round-end bonus, so no special filter needed
   */
  private filterSafeCards(
    evaluatedCards: Array<{ card: Card; evaluation: CardEvaluation }>,
    context: AIDecisionContext
  ): Array<{ card: Card; evaluation: CardEvaluation }> {
    return evaluatedCards.filter(({ card, evaluation }) => {
      // Avoid cards with very high risk in late game
      if (context.gameState.round.roundNumber >= 3 && evaluation.riskLevel > 0.8) {
        return false
      }

      // Prefer cards with good strategic value
      return evaluation.strategicValue > 0.3
    })
  }

  /**
   * Select best card from safe options
   */
  private selectBestCard(
    safeCards: Array<{ card: Card; evaluation: CardEvaluation }>,
    context: AIDecisionContext
  ): { card: Card; evaluation: CardEvaluation } {
    // Sort by strategic value with some randomization for variety
    const sortedCards = [...safeCards].sort((a, b) => {
      // Primary sort by strategic value
      let valueDiff = b.evaluation.strategicValue - a.evaluation.strategicValue

      // Add small random factor to prevent deterministic play
      const randomFactor = (this.probability.random() - 0.5) * 0.1
      valueDiff += randomFactor

      return valueDiff
    })

    // Consider round-specific strategies
    const roundNumber = context.gameState.round.roundNumber

    if (roundNumber === 1) {
      // Early game: prefer cards with high market potential
      sortedCards.sort((a, b) => b.evaluation.marketPotential - a.evaluation.marketPotential)
    } else if (roundNumber >= 3) {
      // Late game: prefer lower risk, established artists
      sortedCards.sort((a, b) => {
        const riskDiff = a.evaluation.riskLevel - b.evaluation.riskLevel
        const artistDiff = this.getArtistRankDifference(a.card.artist, b.card.artist, context)
        return riskDiff + artistDiff * 0.5
      })
    }

    return sortedCards[0]
  }

  /**
   * Get rank difference between artists (lower rank = better)
   */
  private getArtistRankDifference(artistA: Artist, artistB: Artist, context: AIDecisionContext): number {
    const rankA = context.marketAnalysis.artistCompetitiveness[artistA]?.rank || 5
    const rankB = context.marketAnalysis.artistCompetitiveness[artistB]?.rank || 5

    return rankA - rankB
  }

  /**
   * Check if a double card can be played safely without hijack risk
   *
   * A double card is safe if:
   * 1. AI has a matching card to offer (no hijack possible)
   * 2. P(Hijack) < 30% (low risk even without matching card)
   *
   * @param doubleCard - The double card being considered
   * @param hand - AI's current hand
   * @param context - AI decision context
   * @returns true if safe to play, false if risky
   */
  private async checkDoubleCardSafety(
    doubleCard: Card,
    hand: Card[],
    context: AIDecisionContext
  ): Promise<boolean> {
    // Check if AI has a matching card (same artist, not a double)
    const hasMatching = hand.some(c =>
      c?.artist === doubleCard?.artist &&
      c?.auctionType !== 'double' &&
      c?.id !== doubleCard?.id
    )

    // If AI has matching card, safe to play (will offer it)
    if (hasMatching) {
      return true
    }

    // No matching card - check hijack probability
    try {
      const safe = canPlayDoubleSafely(
        doubleCard.artist,
        context.gameState,
        context.playerIndex
      )
      return safe
    } catch {
      // TODO: Monitor if this occurs - hijack calculation failing indicates a bug
      // For now: be conservative and treat as unsafe (don't play the double)
      return false
    }
  }

  /**
   * Alternative card selection method for variety
   */
  async selectCardByStrategy(context: AIDecisionContext): Promise<{ card: Card; evaluation: CardEvaluation } | null> {
    const { gameState, cardEvaluations } = context
    const player = gameState.players[context.playerIndex]

    if (player.hand.length === 0) {
      return null
    }

    // Choose strategy based on game state
    const strategy = this.chooseCardPlayStrategy(context)

    switch (strategy) {
      case 'high_value':
        return await this.selectHighestValueCard(context)
      case 'market_control':
        return await this.selectMarketControlCard(context)
      case 'risk_averse':
        return await this.selectLowRiskCard(context)
      case 'opportunity':
        return await this.selectOpportunityCard(context)
      default:
        return await this.selectOptimalCard(context)
    }
  }

  /**
   * Choose card play strategy based on context
   */
  private chooseCardPlayStrategy(context: AIDecisionContext): 'high_value' | 'market_control' | 'risk_averse' | 'opportunity' {
    const { gameState, marketAnalysis } = context
    const player = gameState.players[context.playerIndex]

    // Late game with low money = risk averse
    if (gameState.round.roundNumber >= 3 && player.money < 30) {
      return 'risk_averse'
    }

    // Emerging market = look for opportunities
    if (marketAnalysis.marketState === 'emerging') {
      return 'opportunity'
    }

    // High volatility = play it safe
    if (marketAnalysis.volatility > 0.7) {
      return 'risk_averse'
    }

    // Default to high value
    return 'high_value'
  }

  /**
   * Select card with highest value
   */
  private async selectHighestValueCard(context: AIDecisionContext): Promise<{ card: Card; evaluation: CardEvaluation } | null> {
    const { gameState } = context
    const player = gameState.players[context.playerIndex]

    if (player.hand.length === 0) {
      return null
    }

    const evaluatedCards = await this.evaluateHand(player.hand, context)
    evaluatedCards.sort((a, b) => b.evaluation.baseValue - a.evaluation.baseValue)

    return evaluatedCards[0] || null
  }

  /**
   * Select card for market control
   */
  private async selectMarketControlCard(context: AIDecisionContext): Promise<{ card: Card; evaluation: CardEvaluation } | null> {
    const { gameState } = context
    const player = gameState.players[context.playerIndex]

    if (player.hand.length === 0) {
      return null
    }

    const evaluatedCards = await this.evaluateHand(player.hand, context)
    evaluatedCards.sort((a, b) => b.evaluation.artistControl - a.evaluation.artistControl)

    return evaluatedCards[0] || null
  }

  /**
   * Select card with lowest risk
   */
  private async selectLowRiskCard(context: AIDecisionContext): Promise<{ card: Card; evaluation: CardEvaluation } | null> {
    const { gameState } = context
    const player = gameState.players[context.playerIndex]

    if (player.hand.length === 0) {
      return null
    }

    const evaluatedCards = await this.evaluateHand(player.hand, context)
    evaluatedCards.sort((a, b) => a.evaluation.riskLevel - b.evaluation.riskLevel)

    return evaluatedCards[0] || null
  }

  /**
   * Select card based on opportunities
   */
  private async selectOpportunityCard(context: AIDecisionContext): Promise<{ card: Card; evaluation: CardEvaluation } | null> {
    const { gameState } = context
    const player = gameState.players[context.playerIndex]

    if (player.hand.length === 0) {
      return null
    }

    const evaluatedCards = await this.evaluateHand(player.hand, context)
    evaluatedCards.sort((a, b) => b.evaluation.marketPotential - a.evaluation.marketPotential)

    return evaluatedCards[0] || null
  }

  /**
   * Initialize with game state
   */
  initialize(gameState: any, playerIndex: number): void {
    this.decisionCount = 0
    this.evCalculationCount = 0
  }

  /**
   * Update with new context
   */
  update(context: AIDecisionContext): void {
    // Medium AI adapts based on market changes
    // Could update evaluation parameters here
  }

  /**
   * Clean up resources
   */
  cleanup(): void {
    // No resources to clean up
  }

  /**
   * Get statistics
   */
  getStats() {
    return {
      decisionsMade: this.decisionCount,
      totalConfidence: this.decisionCount * 0.7, // Approximate
      evCalculations: this.evCalculationCount,
    }
  }

  /**
   * Get EV calculation count
   */
  getEVCalculationCount(): number {
    return this.evCalculationCount
  }

  /**
   * Get decision count
   */
  getDecisionCount(): number {
    return this.decisionCount
  }
}